import React, { useState } from 'react';
import { isEmpty } from 'lodash';
import { resolve } from 'library/helpers/path-resolver';
import { Chart } from 'react-charts';
import PublicQuery from 'library/util/public-query';
import withData from 'util/hoc/with-data';
import Legend from 'apps/roi/components/charts/legend';
import { jsonToInputParams } from 'library/util/graph';
import withConfig from 'util/hoc/with-config';
import { withUser } from 'util/hoc';
import * as StyledRoiChart from 'apps/roi/components/charts/styled-roi-chart';

const RoiChart = ({
  graph_update,
  graph_data,
  graph_loading,
  handle_graph_loading,
  removeDataCallback,
  configuration,
  programMap,
}) => {
  if (graph_loading) return handle_graph_loading();

  const roi_data = resolve(graph_data, 'roi_data', []).sort((a, b) => {
    return (
      b.data[b.data.length - 1].cumulative -
      a.data[a.data.length - 1].cumulative
    );
  });

  const { graphMin, graphMax } = React.useMemo(() => {
    const elapsedTime = 0;
    return {
      graphMin: Math.min(
        ...roi_data.map(({ data }) => data.length + elapsedTime),
        elapsedTime
      ),
      graphMax: Math.max(
        ...roi_data.map(({ data }) => data.length - 1 + elapsedTime),
        elapsedTime + 20
      ),
    };
  }, [roi_data]);

  const colorPalette = resolve(
    configuration,
    'environment/journey/tools/graph_color_palette',
    StyledRoiChart.DefaultColorPalette
  );

  const [state, setState] = useState({
    activeSeriesIndex: -1,
    activeDatumIndex: -1,
    min: graphMin,
    max: graphMax, // get the max date from data
  });
  const { activeSeriesIndex, activeDatumIndex, min, max } = state;

  for (let i = 0; i < roi_data.length; i++) {
    roi_data[i].color = programMap[roi_data[i].id];
  }
  const data = React.useMemo(() => roi_data, [roi_data]);
  const getDatums = React.useCallback((series) => series.data, []);
  const getPrimary = React.useCallback(
    (datum, i, series, seriesIndex, data) => {
      return datum.year + min;
    },
    []
  );
  const getSecondary = React.useCallback((datum) => datum.cumulative, []);

  const axes = React.useMemo(
    () => [
      {
        primary: true,
        type: 'linear',
        position: 'bottom',
        hardMin: min,
        hardMax: max,
        format: (stuff) => `${stuff}`.replace(',', ''),
      },
      { type: 'linear', position: 'left' },
    ],
    [min, max]
  );

  const getSeriesStyle = React.useCallback(
    (series) => {
      let seriesOpacity = 1;
      if (!(series.index === activeSeriesIndex || activeSeriesIndex === -1)) {
        seriesOpacity = 0.3;
      }
      return {
        opacity: seriesOpacity,
        color: series.originalSeries.color,
      };
    },
    [roi_data, activeSeriesIndex]
  );

  const series = React.useMemo(
    () => ({
      type: 'line',
      showPoints: true,
    }),
    []
  );

  const tooltip = {
    align: 'top',
    anchor: 'closest',
    zIndex: 100,
  };

  const getDatumStyle = React.useCallback(
    (datum) => {
      if (datum.xValue > max || datum.xValue < min) return;
      const isActiveIndex = activeDatumIndex === datum.index;
      const isActiveSeries = activeSeriesIndex === datum.seriesIndex;

      let dotSize = 2;
      if (isActiveIndex) {
        // make other dots in the same x axis slightly larger, but the one in the active series the largest
        dotSize = isActiveSeries ? 7 : 5;
      } else if (isActiveSeries) {
        // make all dots in active series a bit larger
        dotSize = 3;
      }
      return {
        r: dotSize,
      };
    },
    [activeDatumIndex, activeSeriesIndex]
  );

  const onFocus = React.useCallback(
    (focused) => {
      setState({
        ...state,
        activeSeriesIndex: focused ? focused.series.id : -1,
        activeDatumIndex: focused ? focused.index : -1,
      });
    },
    [setState]
  );

  const removeDataAtIndex = async (index) => {
    const valueToRemove = roi_data[index];
    roi_data.splice(index, 1);
    await graph_update({ roi_data: roi_data });
    removeDataCallback(valueToRemove, index);
  };

  const primaryCursor = React.useMemo(
    () => ({
      // eslint-disable-next-line react/display-name
      render: (props) => (
        <span style={{ fontSize: '1rem' }}>
          <span role="img" aria-label="icon">
            🕑
          </span>{' '}
          {(props.formattedValue || '').toString().replace(',', '')}
        </span>
      ),
      showLine: false,
    }),
    []
  );

  const secondaryCursor = React.useMemo(
    () => ({
      // eslint-disable-next-line react/display-name
      render: (props) => (
        <span style={{ fontSize: '1rem' }}>
          <span role="img" aria-label="icon">
            💰
          </span>{' '}
          {(props.formattedValue || '').toString()}
        </span>
      ),
      showLine: false,
    }),
    []
  );

  return (
    <StyledRoiChart.ChartPageContainer>
      <StyledRoiChart.ChartContainer>
        <StyledRoiChart.ChartHeader>
          How much is your investment in education worth?
        </StyledRoiChart.ChartHeader>
        <StyledRoiChart.ChartBodyContainer>
          <StyledRoiChart.ChartYAxisLabel>
            Total Net Earnings ($)
          </StyledRoiChart.ChartYAxisLabel>
          <StyledRoiChart.ChartBody>
            <Chart
              data={data}
              getDatums={getDatums}
              getPrimary={getPrimary}
              getSecondary={getSecondary}
              axes={axes}
              series={series}
              onFocus={onFocus}
              activeSeriesIndex={activeSeriesIndex}
              getSeriesStyle={getSeriesStyle}
              getDatumStyle={getDatumStyle}
              tooltip={tooltip}
              primaryCursor={primaryCursor}
              secondaryCursor={secondaryCursor}
            />
          </StyledRoiChart.ChartBody>
        </StyledRoiChart.ChartBodyContainer>
        <StyledRoiChart.ChartXAxisLabel>
          Years since program start
          <p>
            This chart is brought to you by the Colorado Community College
            System in an effort to increase transparency and improve student
            outcomes. The data is based on the tuition and fees the colleges
            reported and the earnings reported by graduates. This graph shows an
            average of total earnings minus program costs over time.
            <br />
            Please let us know what other information would be useful in your
            search by emailing showmethedata@pairin.com.
          </p>
        </StyledRoiChart.ChartXAxisLabel>
      </StyledRoiChart.ChartContainer>
      <Legend
        roi_data={roi_data}
        activeSeriesIndex={activeSeriesIndex}
        removeDataAtIndex={removeDataAtIndex}
        setActiveIndex={(index) =>
          setState({ ...state, activeSeriesIndex: index })
        }
      />
    </StyledRoiChart.ChartPageContainer>
  );
};

RoiChart.defaultProps = {
  removeDataCallback: () => null,
};

RoiChart.dependantPropPath = ['program_ids', 'answers', 'answersCount'];
RoiChart.query = async (token, { programMap, answers, user }) =>
  isEmpty(answers) || isEmpty(user)
    ? PublicQuery(`
    {
      roi_data(${jsonToInputParams({
        program_ids: Object.keys(programMap).map((c) => Number(c)),
      })})
    }
  `)
    : PublicQuery(`
    {
      roi_data(${jsonToInputParams({
        program_ids: Object.keys(programMap).map((c) => Number(c)),
        scholarship_amount: answers.scholarship_amount
          ? parseInt(answers.scholarship_amount)
          : undefined,
        program_contribution: answers.program_contribution
          ? parseInt(answers.program_contribution)
          : undefined,
        full_time_attendance: answers.hasOwnProperty('full_time_attendance')
          ? answers.full_time_attendance
          : undefined,
        uuid: user.uuid,
      })})
    }
  `);

export default withConfig(withUser(withData(RoiChart)));
